import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Runtime } from '@/core/enums/runtime.enum'
import {
  useCardNavigation,
  useGraftability,
  useInstallationConfig,
  useLibraries,
  useScrollToMethod,
  useSetupCommands,
} from '@/core/hooks'
import { useCodeStore } from '@/core/stores/codeStore'
import { useNavigationPath, useNavigationStore, useOpenAccordions } from '@/core/stores/navigationStore'
import { PACKAGE_MANAGER_MAP } from '@/core/utils/installation'

import type { CardData } from '../types'
import { parseBreadcrumbHref } from '../utils'

import { useInstallationTabs } from './useInstallationTabs'

export function useCardData(isMobile: boolean): CardData | null {
  const [isHydrated, setIsHydrated] = useState(false)
  const path = useNavigationPath()
  const openAccordions = useOpenAccordions()
  const previousPathStringRef = useRef<string>('')

  const {
    selectedTechnology,
    suggestedTechnologies,
    technologies,
    technologySetupCommands,
    technologyConfigurations,
    technologyImports,
    technologyLibraryImports,
    setSelectedTechnology,
  } = useCodeStore()

  const {
    gateway,
    gatewayPath,
    gatewayWsPort,
    gatewayMcpPort,
    libraries,
    guid,
    registry,
    gsmuEndpoint,
    projectName,
    isSecured,
    isLoading,
    isError,
  } = useLibraries()

  const { isLoading: isSetupCommandsLoading } = useSetupCommands()

  useEffect(() => {
    setIsHydrated(true)
  }, [])

  const installationCardKey = useMemo(
    (): string => `installation-${path.join('/')}-${selectedTechnology ?? 'none'}`,
    [path, selectedTechnology]
  )

  const handleAccordionToggle = useCallback((itemId: string) => {
    requestAnimationFrame(() => {
      const store = useNavigationStore.getState()
      const isOpen = store.openAccordions.includes(itemId)
      if (isOpen) {
        store.closeAccordion(itemId)
      } else {
        store.openAccordion(itemId)
      }
    })
  }, [])

  const { list, details, breadcrumbs, libraryData, classData, mobileCards } = useCardNavigation({
    path,
    libraries,
    gateway,
    projectName,
    openAccordions,
    onAccordionToggle: handleAccordionToggle,
  })

  useEffect(() => {
    const runtimeName = libraryData?.runtimeName as Runtime | undefined
    if (!runtimeName) return

    const consumerTechnology = PACKAGE_MANAGER_MAP[runtimeName]
    if (!consumerTechnology) return

    useCodeStore.setState({
      suggestedTechnologies: [consumerTechnology],
    })
  }, [libraryData?.id, libraryData?.runtimeName])

  const { setupCommandMap, configurationMap, importCode } = useInstallationConfig({
    selectedTechnology,
    libraryData,
    classData,
    gatewayPath,
    gatewayWsPort,
    gatewayMcpPort,
    guid,
    gsmuEndpoint,
    registry,
    isSecured,
    projectName,
    technologySetupCommands,
    technologyConfigurations,
    technologyImports,
    technologyLibraryImports,
  })

  useEffect(() => {
    const isAtClassLevel = path.length === 2
    const currentPathString = path.join('/')
    const pathChanged = previousPathStringRef.current !== currentPathString

    if (
      !isMobile &&
      isAtClassLevel &&
      details.length > 0 &&
      openAccordions.length === 0 &&
      pathChanged &&
      details[0]?.id
    ) {
      useNavigationStore.getState().openAccordion(details[0].id)
    }
    previousPathStringRef.current = currentPathString
  }, [path, details, openAccordions, isMobile])

  useScrollToMethod()

  const onTechnologyChange = useCallback(
    (technology: string) => setSelectedTechnology(technology),
    [setSelectedTechnology]
  )

  // TODO: Implement connection string generator
  const onConnectionStringGenerator = useCallback(() => {}, [])

  const installationTabs = useInstallationTabs({
    setupCommandMap,
    configurationMap,
    importCode,
    selectedTechnology,
    hostedRuntimeName: libraryData?.runtimeName,
  })

  const graftabilityReport = useGraftability(libraryData)

  const onBreadcrumbClick = useCallback((href: string | undefined) => {
    if (href) {
      useNavigationStore.getState().navigateTo(parseBreadcrumbHref(href))
    }
  }, [])

  const onMobileBack = useCallback(() => {
    useNavigationStore.getState().navigateTo(path.slice(0, -1))
  }, [path])

  const onMobileCardClick = useCallback(
    (methodId: string) => {
      useNavigationStore.getState().navigateTo([...path, methodId])
    },
    [path]
  )

  const onMobileMethodNavigate = useCallback(
    (targetId: string) => {
      useNavigationStore.getState().navigateTo([...path.slice(0, -1), targetId])
    },
    [path]
  )

  const mobileHeaderInfo = useMemo(() => {
    const activeBreadcrumb = breadcrumbs.find((b) => b.active)
    return { label: activeBreadcrumb?.text ?? '' }
  }, [breadcrumbs])

  if (!isHydrated || isError || isLoading || isSetupCommandsLoading) return null

  return {
    path,
    openAccordions,
    installationCardKey,
    technologies,
    suggestedTechnologies,
    selectedTechnology,
    installationTabs,
    graftabilityReport,
    onTechnologyChange,
    onConnectionStringGenerator,
    libraryData,
    classData,
    list,
    details,
    breadcrumbs,
    mobileCards,
    onBreadcrumbClick,
    onMobileBack,
    onMobileCardClick,
    onMobileMethodNavigate,
    mobileHeaderInfo,
  }
}
